import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { useGetMatterWorkflowsQuery } from '@/services/api/workflowService';

// import { WorkflowStatusBadge } from '../shared/WorkflowStatusBadge';

interface PoliceEmptyStateWithWorkflowProps {
  matterId: string;
  onCreateReport?: () => void;
}

export const PoliceEmptyStateWithWorkflow: React.FC<
  PoliceEmptyStateWithWorkflowProps
> = ({ matterId, onCreateReport }) => {
  const { data: workflows = [] } = useGetMatterWorkflowsQuery(matterId, {
    skip: !matterId,
    pollingInterval: 5000, // Poll every 5 seconds for active workflows
  });

  // Check if MatterPoliceBackfillWorkflow is running
  // The workflow ID will be "matter-{matterId}::police-backfill"
  const policeBackfillWorkflow = workflows.find(
    (workflow) =>
      workflow.workflow_type === 'MatterPoliceBackfillWorkflow' ||
      workflow.workflow_id.includes('::police-backfill'),
  );

  // The API returns workflows with ExecutionStatus = "Running"
  // So if we get a workflow, it's running
  const isActivelyScanning = !!policeBackfillWorkflow;

  if (isActivelyScanning) {
    return (
      <div className="text-center py-16 px-6 max-w-md mx-auto">
        <div className="mb-6">
          <MagnifyingGlassIcon className="h-12 w-12 text-blue-500 mx-auto animate-pulse" />
        </div>

        <h3 className="text-lg font-medium text-gray-900 mb-3">
          Actively Scanning for Police Reports
        </h3>

        <p className="text-gray-600 mb-6 leading-relaxed">
          We&apos;re actively scanning this matter (and the web) for information
          that might help us find the police report.
        </p>

        <div className="flex justify-center mb-6">
          <span className="inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium bg-blue-100 text-blue-800 border-blue-200 animate-pulse">
            🔍 Scanning in Progress
          </span>
        </div>

        <p className="text-sm text-gray-500">
          This process may take a few minutes. We&apos;ll automatically update
          this page when we find relevant information.
        </p>
      </div>
    );
  }

  // Default empty state when no workflow is running
  return (
    <div className="text-center py-16 px-6 max-w-md mx-auto">
      <div className="mb-6">
        <MagnifyingGlassIcon className="h-12 w-12 text-gray-400 mx-auto" />
      </div>

      <h3 className="text-lg font-medium text-gray-900 mb-3">
        No Police Reports Found
      </h3>

      <p className="text-gray-600 mb-6 leading-relaxed">
        We haven&apos;t found any police reports for this matter yet. You can
        manually create one or use our discovery tools to search for potential
        reports.
      </p>

      {onCreateReport && (
        <button
          type="button"
          onClick={onCreateReport}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          Create Police Report
        </button>
      )}
    </div>
  );
};
